'use strict';

// Pure, DOM-free rules shared by tracking.js and scripts/growth/analytics_core.test.js.
// Everything that reaches analytics passes through sanitizeEventParams: only allowlisted
// identifiers survive, so financial data or personal data cannot be sent by accident.

const EVENT_NAMES = new Set([
    'landing_view', 'hero_cta_click', 'final_cta_click', 'telegram_click', 'signup_start',
    'related_tool_click', 'faq_expand', 'product_proof_view', 'sign_up', 'activation_completed',
    'calculator_result_saved', 'calculator_share', 'guide_calculator_use'
]);

const ALLOWED_PARAMS = new Set([
    'event_id', 'page_type', 'page_slug', 'page_intent', 'page_segment', 'locale',
    'cta_name', 'cta_position', 'activation_type', 'device_category', 'is_authenticated',
    'traffic_source', 'traffic_medium', 'traffic_campaign',
    'first_touch_source', 'first_touch_medium', 'first_touch_campaign',
    'experiment_id', 'experiment_variant'
]);

const SAFE_VALUE = /^[A-Za-z0-9_.\-/]{1,100}$/;
const CARD_LIKE = /\d{12,}/;
const SEARCH_ENGINES = /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|yandex)\./;
const CLICK_IDS = ['gclid', 'fbclid', 'msclkid'];

function isSafeValue(value) {
    return typeof value === 'string' && SAFE_VALUE.test(value) && !CARD_LIKE.test(value);
}

function sanitizeEventParams(params) {
    const clean = {};
    Object.keys(params || {}).forEach(function (key) {
        if (ALLOWED_PARAMS.has(key) && isSafeValue(params[key])) {
            clean[key] = params[key];
        }
    });
    return clean;
}

function isAllowedEvent(name) {
    return EVENT_NAMES.has(name);
}

// Drops every query parameter except utm_*, which is what keeps emails and tokens out of GA.
function sanitizeLocation(href) {
    try {
        const url = new URL(href);
        const kept = new URLSearchParams();
        url.searchParams.forEach(function (value, key) {
            if (key.indexOf('utm_') === 0 && isSafeValue(value)) {
                kept.set(key, value);
            }
        });
        const query = kept.toString();
        return url.origin + url.pathname + (query ? '?' + query : '');
    } catch (e) {
        return '';
    }
}

function safeToken(value) {
    if (!value) {
        return null;
    }
    const clean = String(value).replace(/[^A-Za-z0-9_.\-]/g, '').substring(0, 100);
    return clean && !CARD_LIKE.test(clean) ? clean : null;
}

function resolveTouch(href, referrer) {
    const url = new URL(href);
    const q = url.searchParams;
    const clickIdType = CLICK_IDS.find(function (id) { return q.has(id); }) || null;
    const touch = {
        s: safeToken(q.get('utm_source')),
        m: safeToken(q.get('utm_medium')),
        c: safeToken(q.get('utm_campaign')),
        ct: safeToken(q.get('utm_content')),
        tm: safeToken(q.get('utm_term')),
        ck: clickIdType,
        rh: null,
        lp: url.pathname
    };

    let referrerHost = null;
    try {
        referrerHost = referrer ? new URL(referrer).hostname : null;
    } catch (e) {
        referrerHost = null;
    }
    const isExternal = referrerHost && referrerHost !== url.hostname;
    if (isExternal) {
        touch.rh = referrerHost;
    }

    // medium=cta/banner marks links between our own pages, not a traffic source
    if (touch.m === 'cta' || touch.m === 'banner') {
        touch.s = touch.m = touch.c = touch.ct = touch.tm = null;
    }

    if (!touch.s) {
        if (clickIdType) {
            touch.s = clickIdType === 'gclid' ? 'google' : clickIdType === 'fbclid' ? 'facebook' : 'bing';
            touch.m = 'cpc';
        } else if (isExternal) {
            touch.s = safeToken(referrerHost);
            touch.m = SEARCH_ENGINES.test(referrerHost) ? 'organic' : 'referral';
        } else {
            touch.s = 'direct';
            touch.m = 'none';
        }
    }
    return touch;
}

// First touch is never replaced. Direct never replaces a known last touch.
function mergeTouches(existingFirst, existingLast, touch, nowIso) {
    const stamped = Object.assign({ at: nowIso }, touch);
    const lastEntry = { at: nowIso, s: touch.s, m: touch.m, c: touch.c };
    return {
        first: existingFirst || stamped,
        last: touch.s === 'direct' && existingLast ? existingLast : lastEntry
    };
}

function parseConsent(raw) {
    try {
        const value = JSON.parse(decodeURIComponent(raw));
        if (value && value.v === 1) {
            return { analytics: value.a === 1, marketing: value.m === 1 };
        }
    } catch (e) {
        // malformed cookie counts as no choice
    }
    return null;
}

function serializeConsent(analytics, marketing, nowIso) {
    return encodeURIComponent(JSON.stringify({ v: 1, a: analytics ? 1 : 0, m: marketing ? 1 : 0, t: nowIso }));
}

module.exports = {
    isAllowedEvent,
    sanitizeEventParams,
    sanitizeLocation,
    resolveTouch,
    mergeTouches,
    parseConsent,
    serializeConsent
};
